import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Star, Camera, X, Quote } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { api, API } from "../lib/api";

export const Stars = ({ value, onChange, size = 18, testid }) => (
  <div className="flex gap-1" data-testid={testid}>
    {[1, 2, 3, 4, 5].map((n) => (
      <button key={n} type="button" disabled={!onChange} onClick={() => onChange?.(n)}
        data-testid={testid ? `${testid}-${n}` : undefined} aria-label={`${n} star${n > 1 ? "s" : ""}`}
        className={onChange ? "transition-transform hover:scale-110" : "cursor-default"}>
        <Star size={size} strokeWidth={1.5} style={{ color: "var(--accent-metal)" }} fill={n <= value ? "var(--accent-metal)" : "none"}/>
      </button>
    ))}
  </div>
);

// Downscale large phone photos before upload so they stay small and fast
const compressImage = (file) => new Promise((resolve) => {
  const img = new Image();
  img.onload = () => {
    const max = 2000, s = Math.min(1, max / Math.max(img.width, img.height));
    const c = document.createElement("canvas");
    c.width = Math.round(img.width * s); c.height = Math.round(img.height * s);
    c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
    c.toBlob((b) => resolve(b ? new File([b], "photo.jpg", { type: "image/jpeg" }) : file), "image/jpeg", 0.88);
    URL.revokeObjectURL(img.src);
  };
  img.onerror = () => resolve(file);
  img.src = URL.createObjectURL(file);
});

function ReviewForm({ onDone }) {
  const { user } = useAuth();
  const empty = { rating: 0, display_name: user?.name || "", product: "", title: "", body: "", permission: false };
  const [f, setF] = useState(empty);
  const [photo, setPhoto] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const preview = React.useMemo(() => (photo ? URL.createObjectURL(photo) : null), [photo]);

  const pick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/^image\/(jpeg|png|webp|heic|heif)$/i.test(file.type) && !/\.(jpe?g|png|webp)$/i.test(file.name)) { toast.error("Please choose a JPG, PNG or WEBP photo"); return; }
    setPhoto(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.rating) { toast.error("Please choose a star rating"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(f).forEach(([k, v]) => fd.append(k, v));
      if (photo) fd.append("photo", await compressImage(photo));
      await api.post("/reviews", fd);
      toast.success("Thank you! Your review was sent and will appear once approved.");
      setF(empty); setPhoto(null); onDone?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send your review");
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} data-testid="review-form" className="bg-white rounded-lg border hairline p-6 sm:p-8 space-y-5">
      <div><Label>Your rating</Label><div className="mt-2"><Stars value={f.rating} onChange={(n) => setF({ ...f, rating: n })} size={26} testid="review-rating"/></div></div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div><Label>Name to show</Label><Input data-testid="review-name" required value={f.display_name} onChange={(e) => setF({ ...f, display_name: e.target.value })}/></div>
        <div><Label>What did we make? <span className="text-[color:var(--text-muted)]">(optional)</span></Label><Input data-testid="review-product" placeholder="e.g. Walnut memorial plaque" value={f.product} onChange={(e) => setF({ ...f, product: e.target.value })}/></div>
      </div>
      <div><Label>Headline <span className="text-[color:var(--text-muted)]">(optional)</span></Label><Input data-testid="review-title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })}/></div>
      <div><Label>Your review</Label><Textarea data-testid="review-body" required rows={5} value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })}/></div>
      <div>
        <Label>Photo of your finished piece <span className="text-[color:var(--text-muted)]">(optional)</span></Label>
        <input ref={fileRef} data-testid="review-photo-input" type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={pick}/>
        {preview ? (
          <div className="mt-2 relative inline-block">
            <img src={preview} alt="your product" data-testid="review-photo-preview" className="h-32 w-32 object-cover rounded-lg border hairline"/>
            <button type="button" data-testid="review-photo-remove" onClick={() => setPhoto(null)} className="absolute -top-2 -right-2 bg-white rounded-full border hairline p-1"><X size={14}/></button>
          </div>
        ) : (
          <Button type="button" variant="outline" data-testid="review-photo-btn" onClick={() => fileRef.current?.click()} className="mt-2 rounded-full"><Camera size={16} className="mr-2"/>Add a photo</Button>
        )}
      </div>
      <label className="flex items-start gap-3 rounded-lg p-4 cursor-pointer" style={{ background: "rgba(197,160,89,0.08)" }}>
        <Checkbox data-testid="review-permission" checked={f.permission} onCheckedChange={(v) => setF({ ...f, permission: !!v })} className="mt-0.5"/>
        <span className="text-sm leading-relaxed">I give EZ JEM Creations permission to post my review{photo ? " and photo" : ""} on their website.</span>
      </label>
      <Button data-testid="review-submit" type="submit" disabled={busy} className="btn-primary rounded-full px-8 py-6">{busy ? "Sending…" : "Send my review"}</Button>
    </form>
  );
}

function ReviewCard({ r }) {
  return (
    <article data-testid={`review-${r.review_id}`} className="bg-white rounded-lg border hairline overflow-hidden card-lift break-inside-avoid mb-6">
      {r.has_photo && <img src={`${API}/reviews/${r.review_id}/photo`} alt={r.product || "finished piece"} loading="lazy" className="w-full object-cover max-h-96"/>}
      <div className="p-6">
        <Stars value={r.rating} size={16}/>
        {r.title && <h3 className="font-serif text-2xl mt-3 leading-tight" style={{ color: "var(--primary)" }}>{r.title}</h3>}
        <p className="mt-3 text-sm leading-relaxed whitespace-pre-line">{r.body}</p>
        <div className="mt-4 text-xs text-[color:var(--text-muted)]">— {r.display_name}{r.product ? ` · ${r.product}` : ""} · {new Date(r.created_at).toLocaleDateString(undefined, { month: "short", year: "numeric" })}</div>
      </div>
    </article>
  );
}

export default function Reviews() {
  const { user, loading } = useAuth();
  const [reviews, setReviews] = useState([]);
  useEffect(() => { api.get("/reviews").then((r) => setReviews(r.data)).catch((e) => console.error("Could not load reviews", e)); }, []);
  const avg = reviews.length ? reviews.reduce((s, r) => s + r.rating, 0) / reviews.length : 0;

  return (
    <div data-testid="reviews-page" className="max-w-6xl mx-auto px-6 lg:px-10 py-16">
      <div className="uppercase text-xs tracking-[0.3em] font-bold" style={{ color: "var(--secondary)" }}>Reviews</div>
      <h1 className="font-serif text-5xl sm:text-6xl mt-3 leading-none" style={{ color: "var(--primary)" }}>Pieces that found their home.</h1>
      {reviews.length > 0 && (
        <div data-testid="reviews-summary" className="mt-5 flex items-center gap-3 text-sm text-[color:var(--text-muted)]">
          <Stars value={Math.round(avg)} size={18}/> {avg.toFixed(1)} from {reviews.length} review{reviews.length > 1 ? "s" : ""}
        </div>
      )}
      <div className="mt-12 grid lg:grid-cols-[1fr_420px] gap-12 items-start">
        <div>
          {reviews.length === 0 ? (
            <div data-testid="reviews-empty" className="rounded-lg border hairline bg-white p-10 text-center">
              <Quote className="mx-auto text-[color:var(--accent-metal)]" size={28}/>
              <p className="mt-3 text-sm text-[color:var(--text-muted)]">No reviews posted yet — be the first to share your piece.</p>
            </div>
          ) : (
            <div className="columns-1 md:columns-2 gap-6" data-testid="reviews-list">{reviews.map((r) => <ReviewCard key={r.review_id} r={r}/>)}</div>
          )}
        </div>
        <div className="lg:sticky lg:top-28">
          <h2 className="font-serif text-3xl mb-4" style={{ color: "var(--primary)" }}>Share your experience</h2>
          {loading ? null : user ? <ReviewForm/> : (
            <div data-testid="review-signin-prompt" className="rounded-lg border hairline bg-white p-8">
              <p className="text-sm leading-relaxed">Please sign in to leave a review and share a photo of your finished piece.</p>
              <div className="mt-5 flex gap-3">
                <Link to="/login"><Button data-testid="review-signin-btn" className="btn-primary rounded-full px-6">Sign in</Button></Link>
                <Link to="/register"><Button variant="outline" className="rounded-full px-6">Create account</Button></Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
